/*
  A set page's header, the game page's GameHeader carried one level down (Robert,
  2026-10-20: make the set and card pages as modern as the game page, "if there's any
  other information that should be added … let's try it").

  One card: the set's logo on a white tile (set logos are drawn for white, as the game
  page's tiles show them), then the set's name and figures:

    - cards graded, the big gold number: the sum of the rows the table below lists, so the
      header and the table cannot disagree;
    - its rank by cards graded among the game's live sets, same-named rows combined exactly
      as the game page's tiles combine them. Only once the game's whole catalogue is in
      (useAllGameSets), because a rank against part of it is a wrong number;
    - its share of the game's grades;
    - how many of the set's cards PCG has graded at all;
    - the grade mix bar, and how much of the set graded a 10.

  Languages and release year sit as pills beside the h1. A figure that cannot be worked
  out yet is left out rather than shown as 0. With no logo the tile shows the game's logo
  instead, so the card never has a hole in it.
*/

import { Link } from 'react-router-dom';

import GradeMixBar from 'components/GradeMixBar';
import {
  HEADER_CARD,
  HEADER_EYEBROW,
  HEADER_GLOW,
  HEADER_TITLE,
  HeaderFigure,
  HeaderPill,
  HeaderRule,
} from 'components/PageHeaderCard';
import { HERO_IMG, LINK, TILE_CHIP } from 'components/styles';
import { formatCount, formatShare, gradeShare, ordinal, setRank, setSummary } from 'lib';
import { cleanSetName } from 'lib/setName';
import type { SetCardRow } from 'lib/population';

import type { GameSetSummaryDto, GameSummaryDto } from 'types/v2/catalog';

interface SetHeaderProps {
  gameCode: string;
  setName: string;
  game: GameSummaryDto | undefined;
  /* The game's sets, as many as have arrived, and whether that is all of them. */
  sets: GameSetSummaryDto[];
  setsComplete: boolean;
  /* The set's cards with their grade counts (v1 /gameInfo …/set), or undefined while loading. */
  cards: SetCardRow[] | undefined;
}

const yearOf = (releaseDate: string | null | undefined): string | null => {
  const match = /^\d{4}/.exec(releaseDate ?? '');
  return match ? match[0] : null;
};

const SetHeader = ({ gameCode, setName, game, sets, setsComplete, cards }: SetHeaderProps) => {
  const key = setName.trim().toLowerCase();
  const rows = sets.filter(set => set.name.trim().toLowerCase() === key);

  const image = rows.find(row => row.imageUrl)?.imageUrl || game?.imageUrl || '';
  const year = rows.map(row => yearOf(row.releaseDate)).find(Boolean) ?? null;
  const languages = Array.from(new Set(rows.map(row => row.language).filter((l): l is string => !!l)));

  const summary = cards ? setSummary(cards) : null;
  /* Before the cards arrive, the catalogue's own figure for the set stands in. */
  const catalogueGraded = rows.some(row => typeof row.cardsGraded === 'number')
    ? rows.reduce((sum, row) => sum + (row.cardsGraded ?? 0), 0)
    : null;
  const graded = summary ? summary.graded : catalogueGraded;

  /*
    Live sets only, as the game page ranks its Top 10: archived sets have a section of their
    own there, so an archived set gets no rank here either (it is not in the field).
  */
  const archived = rows.length > 0 && rows.every(row => row.archived === true);
  const rank = setsComplete
    ? setRank(
        sets
          .filter(set => set.archived !== true)
          .map(set => ({ setName: set.name, cardsGraded: set.cardsGraded, language: set.language })),
        setName
      )
    : null;
  const gameGraded = game?.cardsGraded;
  const share =
    graded !== null && graded > 0 && typeof gameGraded === 'number' && gameGraded > 0
      ? gradeShare(graded, gameGraded)
      : null;

  return (
    <section
      aria-labelledby="set-heading"
      className={`${HEADER_CARD} grid gap-6 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)] md:items-center lg:gap-8`}
      style={HEADER_GLOW}
    >
      <div className="flex h-[120px] items-center justify-center rounded-xl bg-[#ffffff] p-5 sm:h-[180px] sm:p-6 md:h-[200px]">
        {image ? <img src={image} alt="" {...HERO_IMG} className="max-h-full max-w-full object-contain" /> : null}
      </div>

      <div className="min-w-0">
        <p className={HEADER_EYEBROW}>
          Population Report
          {game ? (
            <>
              {' · '}
              <Link to={`/${gameCode}`} className={`${LINK} no-underline hover:underline`}>
                {game.name}
              </Link>
            </>
          ) : null}
        </p>
        {/* Cleaned for display (pop-language-split D13); lookups above keep the raw name. */}
        <h1 id="set-heading" className={`mt-2 ${HEADER_TITLE}`}>
          {cleanSetName(setName)}
        </h1>
        {languages.length > 0 || year || archived ? (
          <p className="mt-3 flex flex-wrap items-center gap-2">
            {languages.map(language => (
              <HeaderPill key={language}>{language}</HeaderPill>
            ))}
            {year ? <HeaderPill>Released {year}</HeaderPill> : null}
            {archived ? <span className={TILE_CHIP}>Archived</span> : null}
          </p>
        ) : null}
        <HeaderRule />

        <dl className="mt-5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {graded !== null ? <HeaderFigure label="Cards graded" value={formatCount(graded)} accent /> : null}
          {rank ? (
            <HeaderFigure
              label={`Rank among ${formatCount(rank.of)} ${game?.name ?? ''} sets`}
              value={ordinal(rank.rank)}
            />
          ) : null}
          {share !== null ? (
            <HeaderFigure label={`Of all ${game?.name ?? 'game'} grades`} value={formatShare(share)} />
          ) : null}
          {summary && summary.cards > 0 ? (
            <HeaderFigure
              label="Cards with grades"
              value={`${formatCount(summary.cardsWithGrades)} / ${formatCount(summary.cards)}`}
              note={`${formatShare(gradeShare(summary.cardsWithGrades, summary.cards))} of the set`}
            />
          ) : null}
        </dl>

        {summary ? (
          <div className="mt-5">
            <GradeMixBar tally={summary.tally} />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SetHeader;
